import debounce from 'debounce';
import * as echarts from 'echarts';
import { useCallback, useEffect, useState } from 'react';

import generalHelper from '../../helpers/general.helper';

type OptionalChart = echarts.ECharts | undefined;

export const useEChartInit = (
  chartRef: React.MutableRefObject<HTMLDivElement>
): OptionalChart => {
  const [chart, setChart] = useState<echarts.ECharts>();
  useEffect(() => {
    if (chartRef.current) {
      const chartElement = chartRef.current;
      setChart(echarts.init(chartElement));

      return () => {
        echarts.dispose(chartElement);
        setChart(null);
      };
    }
    return generalHelper.noop;
  }, [chartRef]);

  return chart;
};

export const useChartLoadingAnimation = (
  chart: OptionalChart,
  loading?: boolean
): void => {
  useEffect(() => {
    if (chart) {
      if (loading) {
        chart.showLoading();
      } else {
        chart.hideLoading();
      }
    }
  }, [chart, loading]);
};

export const useChartEvent = (
  chart: OptionalChart,
  eventName: string,
  callback: (params: unknown) => void
): void => {
  useEffect(() => {
    if (chart) {
      chart.on(eventName, callback);

      return () => {
        chart.off(eventName, callback);
      };
    }
    return generalHelper.noop;
  }, [callback, chart, eventName]);
};

/**
 * There are some states we want to save but don't want it to
 * trigger any re-rendering.
 * For example, the zoom range when users scroll/zoom the chart,
 * it is useful for next on-demand loading, but itself shouldn't
 * trigger any rendering.
 * Therefore, we use a mutable state object to keep track of them.
 */
interface NonReactStates {
  zoomRangeFromRightEnd: [number, number] | null;
}

/**
 * Setup on demand loading when zoom or pan out of the range.
 * it will call onZoomChanged upon setup.
 * It keeps storing the current zoom range and
 * use that range to calculate new range when new data arrives
 * so that the existing data points won't move around.
 */
export const useOnDemandLoading = (
  chart: OptionalChart,
  onLoadMore: () => void,
  dataLength: number,
  onZoomChanged: (range?: [number, number]) => void
): { forgetUserZoomRange: () => void } => {
  const [mutableState, setMutatableState] = useState<NonReactStates>({
    zoomRangeFromRightEnd: null,
  });

  useEffect(() => {
    if (chart) {
      const debouncedLoadMore = debounce(onLoadMore, 200);
      const callback = params => {
        // the params could contain one more a batch of events
        const event = params.batch ? params.batch.at(-1) : params;
        const { start, end } = event;
        mutableState.zoomRangeFromRightEnd = [
          dataLength * (1 - start / 100),
          dataLength * (1 - end / 100),
        ];
        // load more when reach 5% range
        if (start < 5) {
          debouncedLoadMore();
        }
      };
      chart.on('datazoom', callback);
      return () => {
        chart.off('datazoom', callback);
      };
    }
    return generalHelper.noop;
  }, [
    chart,
    mutableState, // cannot put mutableState.zoomRangeFromRightEnd because we don't want to trigger this effect by mutableState
    onLoadMore,
    dataLength,
  ]);

  useEffect(() => {
    if (mutableState.zoomRangeFromRightEnd) {
      onZoomChanged([
        ((dataLength - mutableState.zoomRangeFromRightEnd[0]) / dataLength) *
          100,
        ((dataLength - mutableState.zoomRangeFromRightEnd[1]) / dataLength) *
          100,
      ]);
    } else {
      onZoomChanged();
    }
  }, [mutableState, onZoomChanged, dataLength]);

  const forgetUserZoomRange = useCallback(() => {
    setMutatableState({
      zoomRangeFromRightEnd: null,
    });
  }, []);

  return { forgetUserZoomRange };
};

export const useItemWidth = (
  chart: OptionalChart,
  dataLength: number
): number => {
  const [itemWidth, setItemWidth] = useState<number>(0);

  useEffect(() => {
    if (chart) {
      const renderFinishedCallback = () => {
        const width = chart.getWidth();
        const dataZoom = chart.getOption().dataZoom;
        if (dataZoom && dataZoom.length > 0 && dataZoom[0].type === 'inside') {
          const { start, end } = dataZoom[0];
          const itemCount = (dataLength * (end - start)) / 100;
          setItemWidth(width / itemCount);
        }
      };
      chart.on('finished', renderFinishedCallback);
      return () => {
        chart.off('finished', renderFinishedCallback);
      };
    }
    return generalHelper.noop;
  }, [chart, dataLength]);

  return itemWidth;
};
